import { Scene } from '@babylonjs/core/scene';
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Color4 } from '@babylonjs/core/Maths/math.color';
import { ParticleSystem } from '@babylonjs/core/Particles/particleSystem';
import { Ability } from '../../systems/AbilitySystem';
import { gameEvents } from '../../core/EventBus';
import { GameEvents } from '../../config/events';
import { setMeshVisibility } from '../../core/meshVisibility';

/**
 * ShadowZoneInfo — describes a registered shadow zone for overlap detection.
 */
export interface ShadowZoneInfo {
  /** The mesh representing the shadow zone volume */
  mesh: AbstractMesh;
}

/**
 * Shadow Slip — Minna blends into heavy shadows and becomes invisible (Babylon.js port).
 *
 * When active AND Minna is inside a shadow zone:
 * - Minna's mesh fades to near-invisible (alpha 0.15)
 * - Alert predators (foxes) cannot detect her
 * - A subtle shimmer effect shows she's still there (player feedback)
 * - She can still interact with objects and abilities
 *
 * Toggle activation: press to enable shadow-seeking mode,
 * press again to disengage. Stealth only actually applies
 * when overlapping a ShadowZone.
 */
export class ShadowSlip implements Ability {
  readonly id = 'shadowSlip';
  readonly name = 'Shadow Slip';
  isActive = false;
  cooldownRemaining = 0;
  readonly cooldownDuration = 0.4; // seconds

  private minnaMesh: AbstractMesh;
  private isStealthed = false;
  private elapsedTime = 0;

  /** Target alpha when fully stealthed */
  private readonly stealthAlpha = 0.15;
  /** Current interpolation progress for fade (0=visible, 1=stealthed) */
  private fadeProgress = 0;
  /** Fade duration in seconds */
  private readonly fadeDuration = 0.3;

  /** Shadow zones registered in the scene */
  private shadowZones: ShadowZoneInfo[] = [];

  /** Shimmer particle system that follows Minna while stealthed. */
  private shimmerPS: ParticleSystem | null = null;
  private scene: Scene;

  constructor(scene: Scene, minnaMesh: AbstractMesh) {
    this.scene = scene;
    this.minnaMesh = minnaMesh;
    this.createShimmerParticles();
  }

  private createShimmerParticles(): void {
    // Cool violet smoke + tiny green embers that trail off Minna when
    // she dissolves into shadow. The emitter follows her collider, so
    // the shimmer stays locked to the cat even while she walks through
    // a shadow corridor.
    const ps = new ParticleSystem("shadowSlipShimmer", 90, this.scene);
    ps.emitter = this.minnaMesh;
    ps.minEmitBox = new Vector3(-0.2, -0.1, -0.2);
    ps.maxEmitBox = new Vector3(0.2, 0.4, 0.2);
    ps.minLifeTime = 0.6;
    ps.maxLifeTime = 1.4;
    ps.minSize = 0.12;
    ps.maxSize = 0.38;
    ps.emitRate = 0; // silent until enterStealth()
    ps.direction1 = new Vector3(-0.2, 0.15, -0.2);
    ps.direction2 = new Vector3(0.2, 0.45, 0.2);
    ps.minEmitPower = 0.1;
    ps.maxEmitPower = 0.35;
    ps.gravity = new Vector3(0, 0.08, 0);
    // Violet dissolve + a touch of Minna's ethereal green on the ember.
    ps.color1 = new Color4(0.35, 0.2, 0.55, 0.55);
    ps.color2 = new Color4(0.15, 0.85, 0.45, 0.4);
    ps.colorDead = new Color4(0.05, 0.05, 0.15, 0);
    ps.blendMode = ParticleSystem.BLENDMODE_ADD;
    ps.start();
    this.shimmerPS = ps;
  }

  /**
   * Register shadow zones so the ability knows where stealth applies.
   */
  registerShadowZones(zones: ShadowZoneInfo[]): void {
    this.shadowZones = zones;
  }

  canActivate(): boolean {
    return !this.isActive && this.cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    this.isActive = true;
    this.elapsedTime = 0;

    return true;
  }

  update(deltaTime: number): void {
    if (!this.isActive) return;

    this.elapsedTime += deltaTime;

    // Check if Minna is inside any shadow zone
    const inShadow = this.checkShadowOverlap();

    if (inShadow && !this.isStealthed) {
      this.enterStealth();
    } else if (!inShadow && this.isStealthed) {
      this.exitStealth();
    }

    // Animate fade
    if (this.isStealthed && this.fadeProgress < 1) {
      this.fadeProgress = Math.min(1, this.fadeProgress + deltaTime / this.fadeDuration);
      this.applyStealthVisuals();
    } else if (!this.isStealthed && this.fadeProgress > 0) {
      this.fadeProgress = Math.max(0, this.fadeProgress - deltaTime / (this.fadeDuration * 0.66));
      this.applyStealthVisuals();
    }
  }

  deactivate(): void {
    this.isActive = false;
    this.cooldownRemaining = this.cooldownDuration;

    if (this.isStealthed) {
      this.exitStealth();
    }

    // Immediately restore full visibility
    this.fadeProgress = 0;
    this.applyStealthVisuals();
  }

  /**
   * Whether Minna is currently invisible to enemies.
   */
  get stealthed(): boolean {
    return this.isStealthed;
  }

  /**
   * Whether Minna is currently overlapping any registered shadow zone.
   * Used by EnergySystem for Ethereal Focus recharge in shadows.
   */
  get isInShadowZone(): boolean {
    return this.checkShadowOverlap();
  }

  private enterStealth(): void {
    this.isStealthed = true;
    if (this.shimmerPS) this.shimmerPS.emitRate = 45;
    gameEvents.emit(GameEvents.SHADOW_SLIP_ENTERED);
  }

  private exitStealth(): void {
    this.isStealthed = false;
    if (this.shimmerPS) this.shimmerPS.emitRate = 0;
    gameEvents.emit(GameEvents.SHADOW_SLIP_EXITED);
  }

  dispose(): void {
    if (this.shimmerPS) {
      this.shimmerPS.stop();
      this.shimmerPS.dispose();
      this.shimmerPS = null;
    }
  }

  private applyStealthVisuals(): void {
    // Interpolate visibility.
    const alpha = 1 - (this.fadeProgress * (1 - this.stealthAlpha));
    // IMPORTANT: `minnaMesh` is the collider capsule — its `visibility`
    // does NOT cascade to descendant meshes. The actual rendered cat
    // is in child nodes (`Clone of Mesh_0_primitive*`). Apply to both
    // the parent AND every descendant so stealth actually reads.
    this.minnaMesh.visibility = alpha;
    for (const child of this.minnaMesh.getChildMeshes(false)) {
      setMeshVisibility(child, alpha);
    }

    // Shimmer effect via slight mesh scaling oscillation when stealthed
    if (this.isStealthed) {
      const shimmer = Math.sin(this.elapsedTime * 10) * 0.005;
      this.minnaMesh.scaling.x = 1 + shimmer;
      this.minnaMesh.scaling.y = 1 - shimmer;
    } else if (this.fadeProgress <= 0) {
      // Restore original scale
      this.minnaMesh.scaling.x = 1;
      this.minnaMesh.scaling.y = 1;
    }
  }

  private checkShadowOverlap(): boolean {
    const minnaBounding = this.minnaMesh.getBoundingInfo();
    if (!minnaBounding) return false;

    const minnaBox = minnaBounding.boundingBox;

    for (const zone of this.shadowZones) {
      const zoneBounding = zone.mesh.getBoundingInfo();
      if (!zoneBounding) continue;

      const zoneBox = zoneBounding.boundingBox;

      // AABB intersection test
      if (this.aabbIntersects(minnaBox, zoneBox)) {
        return true;
      }
    }

    return false;
  }

  private aabbIntersects(
    a: { minimumWorld: Vector3Like; maximumWorld: Vector3Like },
    b: { minimumWorld: Vector3Like; maximumWorld: Vector3Like },
  ): boolean {
    return (
      a.minimumWorld.x <= b.maximumWorld.x &&
      a.maximumWorld.x >= b.minimumWorld.x &&
      a.minimumWorld.y <= b.maximumWorld.y &&
      a.maximumWorld.y >= b.minimumWorld.y &&
      a.minimumWorld.z <= b.maximumWorld.z &&
      a.maximumWorld.z >= b.minimumWorld.z
    );
  }
}

/** Minimal vector interface for AABB check (avoids needing full Vector3 import for type). */
interface Vector3Like {
  x: number;
  y: number;
  z: number;
}
